// Iter XX.174 — My Cricket persistent Player Development System.
//
// Three-layer view designed so a Plus player who completed ONE strong
// Coaching Outcome sees a page that already feels valuable — not a
// database of nine equal cards. Sections without grounded data are
// hidden entirely (the backend already omits them). One intentional
// empty state ("first_action_plan_hint") explains how the product
// compounds when a player has coached but not yet earned an action
// plan.
//
// No LLM calls anywhere. Read-only fetch. Plus-gated (backend 402
// bounces non-Plus users; the router should not link here for
// non-Plus).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { isNative } from "../lib/nativePlatform";

function Section({ title, testId, children }) {
  return (
    <section
      data-testid={testId}
      className="mb-8 rounded-2xl border border-neutral-800 bg-neutral-900/40 p-5"
    >
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
        {title}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, testId }) {
  if (!value) return null;
  return (
    <div className="mb-3" data-testid={testId}>
      <div className="text-[11px] text-neutral-500 mb-1">{label}</div>
      <div className="text-[15px] text-neutral-100 leading-relaxed">
        {value}
      </div>
    </div>
  );
}

export default function MyCricket() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");   // loading | ready | plus_required | error
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/plus/my-cricket");
        if (!cancelled) {
          setData(data);
          setState("ready");
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 402) {
          setState("plus_required");
        } else {
          setState("error");
          setErrMsg(e?.message || "Something went wrong.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6" data-testid="my-cricket-loading">
        <div className="max-w-2xl mx-auto text-neutral-500 text-sm">Loading…</div>
      </div>
    );
  }

  if (state === "plus_required") {
    // Iter XX.194 — On Capacitor native, show the same gate copy but
    // replace the "See Gully Plus" CTA with a plain informational
    // note. Play policy: no upgrade / web-purchase CTA in the
    // Android app. Web experience unchanged.
    const native = isNative();
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6" data-testid="my-cricket-plus-gate">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-semibold mb-3">My Cricket</h1>
          <p className="text-neutral-400 mb-6">
            Your persistent development plan lives here — with Gully Plus.
          </p>
          {native ? (
            <p
              data-testid="my-cricket-native-note"
              className="text-sm text-neutral-500"
            >
              Open Gully on the web when you&apos;re next at a computer
              to explore this feature.
            </p>
          ) : (
            <Link
              to="/plus"
              data-testid="my-cricket-plus-link"
              className="inline-block px-5 py-2.5 rounded-xl bg-white text-black text-sm font-medium hover:bg-neutral-200 transition-colors"
            >
              See Gully Plus
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (state === "error" || !data) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6" data-testid="my-cricket-error">
        <div className="max-w-2xl mx-auto text-neutral-500 text-sm">
          {errMsg || "My Cricket couldn't load right now."}
        </div>
      </div>
    );
  }

  const rn = data.right_now || {};
  const mg = data.my_game || {};
  const mj = data.my_journey || {};

  // Fresh Plus user with no grounded data yet — show a single
  // compounding-value explainer, not nine empty cards.
  if (!data.has_content) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6" data-testid="my-cricket-first-run">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-semibold mb-3">My Cricket</h1>
          <p className="text-neutral-400 mb-6 leading-relaxed">
            Your development plan fills in as you play, chat and upload video.
            Start a conversation and Gully will save your first focus, what it
            noticed, and the one thing to try before your next session.
          </p>
          <Link
            to="/chat"
            data-testid="my-cricket-start-cta"
            className="inline-block px-5 py-2.5 rounded-xl bg-white text-black text-sm font-medium hover:bg-neutral-200 transition-colors"
          >
            Open Chat
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6" data-testid="my-cricket-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-semibold mb-1">My Cricket</h1>
        <p className="text-[13px] text-neutral-500 mb-8">Your living development plan.</p>

        {/* ── LAYER 1 — RIGHT NOW ─────────────────────────────── */}
        {(rn.current_focus || rn.what_gully_noticed || rn.current_action_plan
            || rn.first_action_plan_hint) && (
          <Section title="Right now" testId="mc-right-now">
            <Field
              label="Current focus"
              value={rn.current_focus}
              testId="mc-current-focus"
            />
            {rn.what_gully_noticed && (
              <Field
                label="What Gully noticed"
                value={rn.what_gully_noticed.observation}
                testId="mc-what-noticed"
              />
            )}
            {rn.current_action_plan && (
              <Field
                label="Current action plan"
                value={rn.current_action_plan.commitment}
                testId="mc-action-plan"
              />
            )}
            {rn.next_checkpoint && (
              <Field
                label="Next checkpoint"
                value={rn.next_checkpoint.phrasing}
                testId="mc-next-checkpoint"
              />
            )}
            {!rn.current_action_plan && rn.first_action_plan_hint && (
              <div data-testid="mc-first-action-plan-hint"
                    className="mt-3 text-[13px] text-neutral-500 italic leading-relaxed">
                {rn.first_action_plan_hint}
              </div>
            )}
          </Section>
        )}

        {/* ── LAYER 2 — MY GAME ───────────────────────────────── */}
        {(mg.recurring_patterns || mg.strengths || mg.improvements
            || mg.video_reviews || mg.recent_evidence) && (
          <Section title="My game" testId="mc-my-game">
            {mg.recurring_patterns && mg.recurring_patterns.length > 0 && (
              <Field
                label="Recurring patterns"
                testId="mc-recurring-patterns"
                value={
                  <ul className="list-disc list-inside space-y-1">
                    {mg.recurring_patterns.map((p, i) => (
                      <li key={i}>{typeof p === "string" ? p : (p.name || p.pattern || JSON.stringify(p))}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {mg.strengths && mg.strengths.length > 0 && (
              <Field
                label="Strengths"
                testId="mc-strengths"
                value={
                  <ul className="list-disc list-inside space-y-1">
                    {mg.strengths.map((s, i) => (
                      <li key={i}>{typeof s === "string" ? s : (s.name || JSON.stringify(s))}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {mg.improvements && mg.improvements.length > 0 && (
              <Field
                label="Improvements"
                testId="mc-improvements"
                value={
                  <ul className="list-disc list-inside space-y-1">
                    {mg.improvements.map((g, i) => (
                      <li key={i}>{typeof g === "string" ? g : (g.name || JSON.stringify(g))}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {mg.video_reviews && mg.video_reviews.length > 0 && (
              <Field
                label="Video reviews"
                testId="mc-videos"
                value={
                  <ul className="space-y-1 text-[13px] text-neutral-300">
                    {mg.video_reviews.map((v, i) => (
                      <li key={i}>
                        {v.focus_area || "review"} — {(v.created_at || "").slice(0, 10)}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            {mg.recent_evidence && mg.recent_evidence.length > 0 && (
              <Field
                label="Recent evidence"
                testId="mc-recent-evidence"
                value={
                  <ul className="space-y-1 text-[13px] text-neutral-300">
                    {mg.recent_evidence.map((e, i) => (
                      <li key={i}>
                        {e.kind === "match" ? "Match" : "Practice"} — {(e.at || "").slice(0, 10)}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </Section>
        )}

        {/* ── LAYER 3 — MY JOURNEY ───────────────────────────── */}
        {(mj.long_term_goal || mj.progress || mj.milestones) && (
          <Section title="My journey" testId="mc-my-journey">
            {mj.long_term_goal && (
              <Field
                label="Long-term goal"
                value={mj.long_term_goal}
                testId="mc-long-term-goal"
              />
            )}
            {mj.progress && (
              <>
                {mj.progress.biggest_improvement && (
                  <Field
                    label="Biggest improvement"
                    value={mj.progress.biggest_improvement}
                    testId="mc-biggest-improvement"
                  />
                )}
                {typeof mj.progress.total_sessions === "number"
                    && mj.progress.total_sessions > 0 && (
                  <Field
                    label="Sessions logged"
                    value={String(mj.progress.total_sessions)}
                    testId="mc-total-sessions"
                  />
                )}
                {typeof mj.progress.confidence_trend_pct === "number" && (
                  <Field
                    label="Confidence trend"
                    value={`${mj.progress.confidence_trend_pct > 0 ? "+" : ""}${mj.progress.confidence_trend_pct}%`}
                    testId="mc-confidence-trend"
                  />
                )}
              </>
            )}
            {mj.milestones && mj.milestones.length > 0 && (
              <Field
                label="Milestones"
                testId="mc-milestones"
                value={
                  <ul className="space-y-1 text-[13px] text-neutral-300">
                    {mj.milestones.map((m, i) => (
                      <li key={i}>
                        {(m.kind || "milestone").replace(/_/g, " ")}
                        {m.at ? ` — ${String(m.at).slice(0, 10)}` : ""}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </Section>
        )}
      </div>
    </div>
  );
}
